import React from "react";
import { Card, CardContent, Typography, Button } from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Banker } from "../../../validation/banker.validations";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import ConfirmBlockDialog from "../../../common/confirmBlockAlert";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";

interface Props {
  bankers: Banker[];
  onDelete: (emp: Banker) => void;
  onEdit: (emp: Banker) => void;
  onBlock: (emp: Banker) => void;
  user?: any
}

const BankerCardView: React.FC<Props> = ({ bankers, onDelete, onEdit, onBlock, user }) => {
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openStatusDialog, setOpenStatusDialog] = React.useState(false);
  const [selectedBanker, setSelectedBanker] = React.useState<Banker | null>(null);

  const handleDeleteConfirm = () => {
    if (selectedBanker) {
      onDelete(selectedBanker);
    }
    setOpenDeleteDialog(false);
    setSelectedBanker(null);
  };

  const handleStatusConfirm = () => {
    if (selectedBanker) {
      onBlock(selectedBanker);
    }
    setOpenStatusDialog(false);
    setSelectedBanker(null);
  };

  const openDeactivateDialog = (emp: Banker) => {
    setSelectedBanker(emp);
    setOpenStatusDialog(true);
  };

  const openDeleteDialogHandler = (emp: Banker) => {
    setSelectedBanker(emp);
    setOpenDeleteDialog(true);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 pb-6">
        {bankers.map((banker) => {
          const isActive = banker.isActive;
          return (
            <Card key={banker?.id} className="rounded-2xl shadow border border-gray-200">
              <CardContent className={`flex flex-col items-center text-center px-4 py-6 gap-4 ${!isActive ? "bg-gray-200" : ""}`}>
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center shadow">
                  <PersonIcon className="text-gray-400 text-6xl" />
                </div>

                <Typography variant="h6" className="font-semibold text-gray-900">
                  {`${(banker as any).firstName || ''} ${(banker as any).middleName || ''} ${(banker as any).lastName || ''}`.trim() || (banker as any).name || "-"}
                </Typography>

                <div className="text-sm text-gray-700 space-y-2 text-left w-full">
                  <div className="flex items-center gap-2">
                    <EmailIcon fontSize="small" />
                    <span>
                      <strong>Email:</strong> {banker.email || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon fontSize="small" />
                    <span>
                      <strong>Phone:</strong> {banker.phone ? formatPhoneNumber(banker.phone) : "-"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full mt-4">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    color="secondary"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onEdit(banker)}
                    className="md:col-span-2"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={isActive ? <BlockIcon /> : <EnableIcon />}
                    color={isActive ? "warning" : "success"}
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeactivateDialog(banker)}
                  >
                    {isActive ? "Deactivate" : "Enable"}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeleteDialogHandler(banker)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        itemName={selectedBanker?.name || ""}
      />

      <ConfirmBlockDialog
        open={openStatusDialog}
        isActive={!!selectedBanker?.isActive}
        onClose={() => setOpenStatusDialog(false)}
        onConfirm={handleStatusConfirm}
        itemName={selectedBanker?.name}
      />
    </>
  );
};

export default BankerCardView;
